import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../../common/sharedFunctions';
import { Car, Mail, Phone, MapPin } from 'lucide-react';

const ModernFooter = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const settings = useSelector(state => state.settingsdata.settings);
  const currentYear = new Date().getFullYear();

  const productLinks = [
    { label: 'Taxi Rides', path: '/addbookings' },
    { label: 'Delivery', path: '/addbookings' },
    { label: 'Book Now', path: '/addbookings' },
    { label: 'Driver App', path: '/login' },
  ];

  const companyLinks = [
    { key: 'about_us', path: '/about-us' },
    { key: 'contact_us', path: '/contact-us' },
    { key: 'privacy_policy', path: '/privacy-policy' },
    { key: 'term_condition', path: '/term-condition' },
  ];

  return (
    <footer className="bg-black border-t border-white/5" style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Main */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/gruwcer-logo.png" alt="Gruwcer Delivery" className="h-9 w-auto object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <p className="text-white/40 text-sm leading-relaxed max-w-xs" style={{ fontFamily: FONT_FAMILY }}>
              Your go-to platform for rides and deliveries. Fast, safe, and always available.
            </p>

            {/* Contact */}
            <div className="mt-6 space-y-2">
              {settings?.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 text-white/40 hover:text-[#009689] text-sm transition-colors" style={{ fontFamily: FONT_FAMILY }}>
                  <Mail className="w-4 h-4" />
                  {settings.contact_email}
                </a>
              )}
              {settings?.CompanyPhone && (
                <a href={`tel:${settings.CompanyPhone}`} className="flex items-center gap-2 text-white/40 hover:text-[#009689] text-sm transition-colors" style={{ fontFamily: FONT_FAMILY }}>
                  <Phone className="w-4 h-4" />
                  {settings.CompanyPhone}
                </a>
              )}
              {settings?.CompanyAddress && (
                <div className="flex items-center gap-2 text-white/40 text-sm" style={{ fontFamily: FONT_FAMILY }}>
                  <MapPin className="w-4 h-4 shrink-0" />
                  {settings.CompanyAddress}
                </div>
              )}
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/60 mb-5" style={{ fontFamily: FONT_FAMILY }}>
              Product
            </h4>
            <ul className="space-y-3">
              {productLinks.map((item, i) => (
                <li key={i}>
                  <button
                    onClick={() => navigate(item.path)}
                    className="text-white/40 hover:text-white text-sm transition-colors"
                    style={{ fontFamily: FONT_FAMILY }}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/60 mb-5" style={{ fontFamily: FONT_FAMILY }}>
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.key}>
                  <button
                    onClick={() => navigate(item.path)}
                    className="text-white/40 hover:text-white text-sm transition-colors"
                    style={{ fontFamily: FONT_FAMILY }}
                  >
                    {t(item.key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Get the App */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/60 mb-5" style={{ fontFamily: FONT_FAMILY }}>
              Get the App
            </h4>
            <div className="space-y-3">
              {settings?.AppleStoreLink && (
                <a
                  href={settings.AppleStoreLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
                >
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] text-white/50" style={{ fontFamily: FONT_FAMILY }}>Download on the</p>
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: FONT_FAMILY }}>App Store</p>
                  </div>
                </a>
              )}
              {settings?.PlayStoreLink && (
                <a
                  href={settings.PlayStoreLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
                >
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] text-white/50" style={{ fontFamily: FONT_FAMILY }}>GET IT ON</p>
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: FONT_FAMILY }}>Google Play</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-xs" style={{ fontFamily: FONT_FAMILY }}>
              © {currentYear} {settings?.CompanyName || 'Gruwcer Delivery'}. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => navigate('/privacy-policy')} className="text-white/30 hover:text-white/60 text-xs transition-colors" style={{ fontFamily: FONT_FAMILY }}>
                Privacy
              </button>
              <button onClick={() => navigate('/term-condition')} className="text-white/30 hover:text-white/60 text-xs transition-colors" style={{ fontFamily: FONT_FAMILY }}>
                Terms
              </button>
              <button onClick={() => navigate('/contact-us')} className="text-white/30 hover:text-white/60 text-xs transition-colors" style={{ fontFamily: FONT_FAMILY }}>
                Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ModernFooter;
